import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell } from "lucide-react";
import { toast } from "sonner";

interface Prefs {
  reminders: boolean;
  invitations: boolean;
  announcements: boolean;
  broadcasts: boolean;
}

const DEFAULTS: Prefs = {
  reminders: true,
  invitations: true,
  announcements: true,
  broadcasts: true,
};

const ROWS: Array<{ key: keyof Prefs; label: string; description: string }> = [
  { key: "reminders", label: "Reminders", description: "Upcoming sessions, calendar events, deadlines." },
  { key: "invitations", label: "Invitations", description: "New tests, parent connect requests, course invites." },
  { key: "announcements", label: "Announcements", description: "Per-event updates from your instructor or org." },
  { key: "broadcasts", label: "Platform broadcasts", description: "System-wide messages from administrators." },
];

export default function NotificationCategoryPrefs() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Prefs>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("notification_user_prefs")
        .select("reminders, invitations, announcements, broadcasts")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) setPrefs(data as Prefs);
      setLoading(false);
    })();
  }, [user]);

  const update = async (key: keyof Prefs, value: boolean) => {
    if (!user) return;
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    const { error } = await supabase
      .from("notification_user_prefs")
      .upsert({ user_id: user.id, ...next }, { onConflict: "user_id" });
    if (error) {
      toast.error("Could not save preference");
      setPrefs(prefs);
    }
  };

  if (loading) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification categories
        </CardTitle>
        <CardDescription>
          Choose which kinds of push and in-app notifications you want to receive.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {ROWS.map((r) => (
          <div key={r.key} className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor={`pref-${r.key}`} className="text-sm font-medium">
                {r.label}
              </Label>
              <p className="text-xs text-muted-foreground">{r.description}</p>
            </div>
            <Switch
              id={`pref-${r.key}`}
              checked={prefs[r.key]}
              onCheckedChange={(v) => update(r.key, v)}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
